import { createSlice } from '@reduxjs/toolkit'
import { INews } from 'models/News/INews'
import { INewsCategory } from 'models/News/INewsCategory'
import { INewsDetails } from 'models/News/INewsDetails'
import ITag from 'models/Common/ITag'

export interface NewsState {
  isLoading: boolean
  isLoadingPaginatedNews: boolean
  isLoadingNewsByTags: boolean
  isGetLatestNewsSuccess: boolean
  isGetNewsCountSuccess: boolean
  isGetNewsCategoriesSuccess: boolean
  isGetNewsTagsSuccess: boolean
  isGetNewsDetailsSuccess: boolean
  isGetNewsByTagsSuccess: boolean
  latestNewsData: INews[] | null
  paginatedNewsData: INews[] | null
  newsCategories: INewsCategory[] | null
  newsTags: ITag[] | null
  newsDetails: INewsDetails[] | null
  newsCount: number
  newsByTags: INews[] | null
}

const initialState: NewsState = {
  isLoading: false,
  isLoadingPaginatedNews: false,
  isLoadingNewsByTags: false,
  isGetLatestNewsSuccess: false,
  isGetNewsCountSuccess: false,
  isGetNewsCategoriesSuccess: false,
  isGetNewsTagsSuccess: false,
  isGetNewsDetailsSuccess: false,
  isGetNewsByTagsSuccess: false,
  latestNewsData: null,
  paginatedNewsData: null,
  newsCategories: null,
  newsTags: null,
  newsDetails: null,
  newsCount: 0,
  newsByTags: null,
}

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    apiRequest: (state) => {
      state.isLoading = true
    },
    apiFailure: (state) => {
      state.isLoading = false
      state.isLoadingPaginatedNews = false
      state.isLoadingNewsByTags = false
    },
    paginatedNewsRequest: (state) => {
      state.isLoadingPaginatedNews = true
    },
    newsByTagsRequest: (state) => {
      state.isLoadingNewsByTags = true
    },
    clearNewsDetails: (state) => {
      state.isGetNewsDetailsSuccess = false
      state.newsDetails = null
    },
    clearNewsByTags: (state) => {
      state.isGetNewsByTagsSuccess = false
      state.newsByTags = null
    },
    getLatestNews(state, action) {
      state.isLoading = false
      state.isGetLatestNewsSuccess = true
      state.latestNewsData = action.payload.latestNewsData.result
    },
    getNewsCount(state, action) {
      state.isLoading = false
      state.isGetNewsCountSuccess = true
      state.newsCount = action.payload.newsCount.result.length
    },
    getNewsTags(state, action) {
      state.isLoading = false
      state.isGetNewsTagsSuccess = true
      state.newsTags = action.payload.newsTags.result
    },
    getNewsCategories(state, action) {
      state.isLoading = false
      state.isGetNewsCategoriesSuccess = true
      state.newsCategories = action.payload.newsCategories.result
    },
    getNewsDetails(state, action) {
      state.isLoading = false
      state.isGetNewsDetailsSuccess = true
      state.newsDetails = action.payload.newsDetails.result
    },
    getNewsByTags(state, action) {
      state.isLoadingNewsByTags = false
      state.isGetNewsByTagsSuccess = true
      state.newsByTags = action.payload.newsByTags.result
    },
    getPaginatedNewsSearch(state, action) {
      state.isLoadingPaginatedNews = false
      state.paginatedNewsData = action.payload.paginatedNews.result
    },
    getPaginatedNewsLoadMore(state, action) {
      state.isLoadingPaginatedNews = false

      const existingIds = new Set(
        state.paginatedNewsData?.map((news) => news.id) || []
      )
      const newUniqueNews = action.payload.paginatedNews.result.filter(
        (news: any) => !existingIds.has(news.id)
      )

      state.paginatedNewsData = [
        ...(state.paginatedNewsData || []),
        ...newUniqueNews,
      ]
    },
  },
})

export const {
  apiRequest,
  apiFailure,
  paginatedNewsRequest,
  newsByTagsRequest,
  clearNewsDetails,
  clearNewsByTags,
  getLatestNews,
  getNewsCount,
  getPaginatedNewsSearch,
  getPaginatedNewsLoadMore,
  getNewsCategories,
  getNewsTags,
  getNewsDetails,
  getNewsByTags,
} = newsSlice.actions

export default newsSlice.reducer
